import {useEffect, useLayoutEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'

import {ANSWER_PLACEHOLDER} from './EnrichQuestionCard'
import {markRanges} from './EnrichInterviewView'
import {DecisionButtons, PlaybackRows} from './RecordingDecision'
import {ReadModeSwitch, ReadingBody, RecordControls, readingHint} from './RecordVersionPanel'
import {INTERVIEW_PHASE, INTERVIEW_STEP} from './hooks/useEnrichInterview'
import {READ_MODES, RECORD_VERSION_COPY, TAKE_RESULTS} from './hooks/useRecordVersion'
import useScrollLock from './hooks/useScrollLock'
import {CheckIcon, CloseIcon, PointsIcon, ScriptIcon} from './icons'

const NUMBER_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six']

export const TAKEOVER_COPY = {
  label: 'Add detail to your story',
  loading: {title: 'Reading your story\u2026', sub: 'Looking for a few details worth asking about.'},
  // No "I read your story": it reads as if someone else had (privacy).
  lead: count => (count === 1 ? 'One quick question to add a little more detail. Skip it if you like.' : `${NUMBER_WORDS[count] || count} quick questions to add a little more detail. Skip any you like.`),
  writing: {title: 'Adding your answers to your story\u2026', sub: 'This only takes a moment.'},
  result: {title: 'Here\u2019s your story with your details added.', sub: 'New details from your answers are in green. Read it over and make sure it still sounds like you.'},
  error: {title: 'We couldn\u2019t finish writing your story.', sub: 'Your answers are still here, and your story hasn\u2019t changed.'},
  done: {
    updated: 'Your story is updated.',
    allSet: 'All set.',
    answers: count => `Your answers to ${count === 1 ? 'one question are' : `${(NUMBER_WORDS[count] || count).toLowerCase()} questions are`} part of your story now.`,
    unchanged: 'Your story is just as you wrote it.',
    recorded: {
      [TAKE_RESULTS.REPLACED]: 'Your new recording replaces your current one.',
      [TAKE_RESULTS.KEPT]: 'You kept your current recording.',
      [TAKE_RESULTS.SAVED]: 'Your recording is saved with it.'
    }
  },
  // What the closing note adds for the recording (the done screen's wording
  // reads "with it"; on the page the story is named).
  closing: {
    [TAKE_RESULTS.REPLACED]: 'Your new recording replaces your current one.',
    [TAKE_RESULTS.KEPT]: 'You kept your current recording.',
    [TAKE_RESULTS.SAVED]: 'Your recording is saved with this story.'
  },
  record: {
    title: 'Want your family to hear you tell it?',
    subWithCurrent: 'You\u2019ll hear it back and choose which recording to keep.',
    sub: 'It only takes a minute or two.',
    choices: {
      [READ_MODES.SCRIPT]: {title: 'Read it word for word', detail: 'Your story scrolls in large type, like a teleprompter.'},
      [READ_MODES.POINTS]: {title: 'Use talking points', detail: 'Glance at the key moments and tell it your own way.'}
    },
    notNow: 'Not now'
  },
  progress: {loading: 'Reading your story', question: (index, count) => `Question ${index + 1} of ${count}`, story: 'Your story', voice: 'Your voice', done: 'All set'}
}

// The recording screens after "Use this version" (prototype rec-choice,
// record, rec-decide); `recording` is false until the writer chooses to.
export const REC_STEP = {CHOICE: 'choice', RECORD: 'record', DECIDE: 'decide', DONE: 'done'}

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

const BackIcon = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
    <path d='m15 18-6-6 6-6' />
  </svg>
)

const fit = textarea => {
  if (!textarea) return

  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

/** Where the progress bar stands: `{position, total, label}` (prototype `setProg`). */
export const progressFor = ({phase, items, active, recStep = REC_STEP.DONE}) => {
  const count = items ? items.length : 3
  const total = count + 2

  switch (phase) {
    case INTERVIEW_PHASE.LOADING:
      return {position: 0, total, label: TAKEOVER_COPY.progress.loading}
    case INTERVIEW_PHASE.ASK:
      return {position: Math.max(active, 0), total, label: TAKEOVER_COPY.progress.question(Math.max(active, 0), count)}
    case INTERVIEW_PHASE.DONE:
      if (recStep !== REC_STEP.DONE) return {position: count + 1, total, label: TAKEOVER_COPY.progress.voice}

      return {position: total, total, label: TAKEOVER_COPY.progress.done}
    default:
      return {position: count, total, label: TAKEOVER_COPY.progress.story}
  }
}

/**
 * The Enrich interview on a phone (TK-3204, prototype "focus" takeover): one
 * job per screen, one way forward. The page's own Skip/Next sit behind the
 * takeover, so Next only ever means the next question.
 *
 * Screens: reading the story, one question at a time (lead line, "You said",
 * the question in large type, the answer box, chips), writing, the woven
 * story with its new details in green, a write failure, and done. The bar on
 * top has Back (a question back, or from the result or a failed write to the
 * last question), progress, and Close; Escape closes too. Focus is trapped
 * inside and the page behind does not scroll.
 *
 * "Use this version" leads into recording the new story ("Record this
 * version", when `recordVersion` is given): a choice of reading it word for
 * word or from talking points, the recording screen, and — with a recording
 * already on the story — the choice between the two takes. Close is off
 * while a take is in progress; closing on that choice parks the new take as
 * a draft. The done screen and the closing note say what became of it.
 */
const EnrichTakeover = ({interview, finePointer, recordVersion, storyText, currentRecording}) => {
  const {phase, items, active, activeItem, marks, woven, accepted, answeredCount} = interview
  const rootRef = useRef(null)
  const headingRef = useRef(null)
  const answerRef = useRef(null)
  const goRef = useRef(null)
  const useButtonRef = useRef(null)
  const [chosenRecStep, setChosenRecStep] = useState(REC_STEP.CHOICE)

  const count = items ? items.length : 0
  const asking = phase === INTERVIEW_PHASE.ASK && Boolean(activeItem)
  const writeFailed = phase === INTERVIEW_PHASE.ERROR && Boolean(interview.error) && interview.error.step === INTERVIEW_STEP.WRITE
  const hasAnswer = Boolean(activeItem && activeItem.answer.trim())

  const offeringRecording = phase === INTERVIEW_PHASE.DONE && accepted && Boolean(recordVersion)
  const recorderBusy = Boolean(recordVersion && recordVersion.recorder.busy)
  // Mid-take the takeover can't be left; a Start still waiting on the
  // microphone is cancelled by leaving instead.
  const recorderLocked = Boolean(recordVersion && recordVersion.recorder.locked)
  const recStep = (() => {
    if (!offeringRecording) return REC_STEP.DONE
    if (recordVersion.deciding) return REC_STEP.DECIDE
    if (recordVersion.result) return REC_STEP.DONE

    return chosenRecStep
  })()
  const canBack = (phase === INTERVIEW_PHASE.ASK && active > 0) ||
    phase === INTERVIEW_PHASE.RESULT ||
    writeFailed ||
    (recStep === REC_STEP.RECORD && !recorderLocked)

  // The recorder starts fresh with each pass through "Use this version".
  useEffect(() => {
    if (offeringRecording) recordVersion.beginTakeover()
  }, [offeringRecording])

  // The page behind must not scroll under the takeover. The hold is shared
  // with the phone refine sheet's, which hands over to this in one update.
  useScrollLock(rootRef)

  // Each screen starts at the top with focus on its one job: the answer box
  // on a question, the heading elsewhere, the one button on the done screen.
  useEffect(() => {
    const main = rootRef.current && rootRef.current.querySelector('.ls-to-main')

    if (main) main.scrollTop = 0

    if (asking && answerRef.current) answerRef.current.focus()
    else if (phase === INTERVIEW_PHASE.DONE && recStep !== REC_STEP.CHOICE && recStep !== REC_STEP.DECIDE && goRef.current) goRef.current.focus()
    // On the recording choice the Use button takes focus itself.
    else if (recStep !== REC_STEP.DECIDE && headingRef.current) headingRef.current.focus()
  }, [phase, active, asking, recStep])

  // Close, from the bar, the done screen or Escape. Never mid-take; on the
  // recording choice the new take is parked as a draft (that note stands on
  // its own); otherwise the closing note says what the interview did and
  // what became of the recording.
  const handleClose = () => {
    if (recorderLocked) return

    if (recordVersion) recordVersion.close()

    if (recStep === REC_STEP.DECIDE) interview.cancel({silent: true})
    else interview.cancel({addendum: (recordVersion && recordVersion.result && TAKEOVER_COPY.closing[recordVersion.result]) || null})
  }

  useEffect(() => {
    const handleEscape = event => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => document.removeEventListener('keydown', handleEscape)
  })

  useLayoutEffect(() => fit(answerRef.current), [activeItem && activeItem.answer, active])

  // Tab stays inside the takeover.
  const handleKeyDown = event => {
    if (event.key !== 'Tab' || !rootRef.current) return

    const focusable = Array.from(rootRef.current.querySelectorAll(FOCUSABLE))

    if (!focusable.length) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const handleAnswerKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      handleGo()
    }
  }

  // The one button: Next / Skip / Write my story, or Done when the last
  // question goes by with nothing answered at all.
  const handleGo = () => {
    // Enter in the answer box comes here too, so the button's disabled state
    // (a refused write awaiting a changed answer) has to be honoured here.
    if (interview.goDisabled) return

    if (interview.goLabel === 'Done') interview.finish()
    else interview.go()
  }

  const handleExample = example => {
    interview.addExample(example)
    requestAnimationFrame(() => {
      const textarea = answerRef.current

      if (!textarea) return

      textarea.focus()
      textarea.setSelectionRange(textarea.value.length, textarea.value.length)
    })
  }

  // A question back; from the result or a failed write, the last question;
  // from the recording screen, the choice of how to read it.
  const handleBack = () => {
    if (recStep === REC_STEP.RECORD) {
      // Back from the recording screen forgets a Start still waiting on the mic.
      if (recordVersion.recorder.starting) recordVersion.recorder.cancelStart()
      setChosenRecStep(REC_STEP.CHOICE)
    } else if (phase === INTERVIEW_PHASE.ASK) {
      interview.jump(-1)
    } else {
      interview.backToQuestions({open: true})
    }
  }

  const chooseReading = mode => {
    recordVersion.chooseMode(mode)
    setChosenRecStep(REC_STEP.RECORD)
  }

  const progress = progressFor({phase, items, active, recStep})

  const renderWoven = () => {
    const parts = []
    let at = 0

    markRanges(woven, marks).forEach((range, i) => {
      parts.push(woven.slice(at, range.start))
      parts.push(<ins key={i}>{woven.slice(range.start, range.end)}</ins>)
      at = range.end
    })
    parts.push(woven.slice(at))

    return parts
  }

  const renderMain = () => {
    if (phase === INTERVIEW_PHASE.LOADING) {
      return (
        <div className='ls-f-step ls-f-center'>
          <span className='ls-spin big' />
          <h2 className='ls-f-h' ref={headingRef} tabIndex={-1}>{TAKEOVER_COPY.loading.title}</h2>
          <p className='ls-f-sub'>{TAKEOVER_COPY.loading.sub}</p>
        </div>
      )
    }

    if (asking) {
      return (
        <div className='ls-f-step' key={active}>
          {active === 0 && <p className='ls-f-lead'>{TAKEOVER_COPY.lead(count)}</p>}
          {activeItem.quote && <p className='ls-f-quote'><span>You said</span>&ldquo;{activeItem.quote}&rdquo;</p>}
          <h2 className='ls-f-q' id='ls-to-question' ref={headingRef} tabIndex={-1}>{activeItem.text}</h2>
          <div className='ls-f-field'>
            <label className='sr-only' htmlFor='ls-to-answer'>Your answer</label>
            <textarea
              id='ls-to-answer'
              placeholder={ANSWER_PLACEHOLDER}
              ref={answerRef}
              rows={3}
              value={activeItem.answer}
              onChange={event => interview.setAnswer(event.target.value)}
              onKeyDown={handleAnswerKeyDown}
            />
          </div>
          {
            activeItem.examples.length > 0 &&
              <div aria-label='Tap an answer to start with it' className='ls-f-chips' role='group'>
                {
                  activeItem.examples.map(example => (
                    <button className='ls-f-chip' key={example} type='button' onClick={() => handleExample(example)}>{example}</button>
                  ))
                }
              </div>
          }
        </div>
      )
    }

    if (phase === INTERVIEW_PHASE.WRITING) {
      return (
        <div className='ls-f-step'>
          <h2 className='ls-f-h' ref={headingRef} tabIndex={-1}>{TAKEOVER_COPY.writing.title}</h2>
          <p className='ls-f-sub'>{TAKEOVER_COPY.writing.sub}</p>
          <div className='ls-f-story streaming' />
        </div>
      )
    }

    if (phase === INTERVIEW_PHASE.RESULT) {
      return (
        <div className='ls-f-step'>
          <h2 className='ls-f-h' ref={headingRef} tabIndex={-1}>{TAKEOVER_COPY.result.title}</h2>
          <p className='ls-f-sub'>{TAKEOVER_COPY.result.sub}</p>
          <div className='ls-f-story'>{renderWoven()}</div>
        </div>
      )
    }

    if (phase === INTERVIEW_PHASE.ERROR) {
      return (
        <div className='ls-f-step'>
          <h2 className='ls-f-h' ref={headingRef} tabIndex={-1}>{TAKEOVER_COPY.error.title}</h2>
          <p className='ls-f-sub'>{interview.error && !interview.error.retry ? interview.errorMessage : TAKEOVER_COPY.error.sub}</p>
        </div>
      )
    }

    if (recStep === REC_STEP.CHOICE) {
      return (
        <div className='ls-f-step'>
          <h2 className='ls-f-h' ref={headingRef} tabIndex={-1}>{TAKEOVER_COPY.record.title}</h2>
          <p className='ls-f-sub'>{currentRecording ? TAKEOVER_COPY.record.subWithCurrent : TAKEOVER_COPY.record.sub}</p>
          <div className='ls-f-choices'>
            {
              [READ_MODES.SCRIPT, READ_MODES.POINTS].map(mode => (
                <button className='ls-f-choice' key={mode} type='button' onClick={() => chooseReading(mode)}>
                  <span aria-hidden='true' className='ls-fc-ico'>{mode === READ_MODES.SCRIPT ? <ScriptIcon /> : <PointsIcon />}</span>
                  <span>
                    <span className='ls-fc-t'>{TAKEOVER_COPY.record.choices[mode].title}</span>
                    <span className='ls-fc-d'>{TAKEOVER_COPY.record.choices[mode].detail}</span>
                  </span>
                </button>
              ))
            }
          </div>
        </div>
      )
    }

    if (recStep === REC_STEP.RECORD) {
      return (
        <div className='ls-f-step'>
          <h2 className='sr-only' ref={headingRef} tabIndex={-1}>{RECORD_VERSION_COPY.title}</h2>
          <ReadModeSwitch short className='ls-f-seg' disabled={recorderBusy} mode={recordVersion.mode} onChange={recordVersion.chooseMode} />
          <div className='ls-f-read'>
            <ReadingBody mode={recordVersion.mode} points={recordVersion.points} pointsLoading={recordVersion.pointsLoading} text={storyText || ''} />
          </div>
        </div>
      )
    }

    if (recStep === REC_STEP.DECIDE) {
      return (
        <div className='ls-f-step'>
          <h2 className='ls-f-h' ref={headingRef} tabIndex={-1}>{RECORD_VERSION_COPY.decideTitle}</h2>
          <p className='ls-f-sub'>{RECORD_VERSION_COPY.decideSub}</p>
          <PlaybackRows current={currentRecording} take={recordVersion.take} />
        </div>
      )
    }

    if (phase === INTERVIEW_PHASE.DONE) {
      const bits = [
        accepted && TAKEOVER_COPY.done.answers(answeredCount),
        recordVersion && recordVersion.result && TAKEOVER_COPY.done.recorded[recordVersion.result]
      ].filter(Boolean)

      return (
        <div className='ls-f-step ls-f-center'>
          <span aria-hidden='true' className='ls-f-check'><CheckIcon size={28} /></span>
          <h2 className='ls-f-h' ref={headingRef} tabIndex={-1}>{accepted ? TAKEOVER_COPY.done.updated : TAKEOVER_COPY.done.allSet}</h2>
          <p className='ls-f-sub'>{bits.length ? bits.join(' ') : TAKEOVER_COPY.done.unchanged}</p>
        </div>
      )
    }

    return null
  }

  const renderFooter = () => {
    if (asking) {
      return (
        <>
          {finePointer && hasAnswer && <span className='ls-f-hint'>Press <kbd>Enter</kbd> to continue</span>}
          <button className={`ls-f-go${interview.goPrimary ? '' : ' ghost'}`} disabled={interview.goDisabled} type='button' onClick={handleGo}>{interview.goLabel}</button>
        </>
      )
    }

    if (phase === INTERVIEW_PHASE.RESULT) {
      return (
        <>
          <button className='ls-f-text' type='button' onClick={interview.finish}>Keep my original</button>
          <button className='ls-f-go' type='button' onClick={interview.accept}>Use this version</button>
        </>
      )
    }

    if (phase === INTERVIEW_PHASE.ERROR) {
      return (
        <>
          <button className='ls-f-text' type='button' onClick={handleClose}>Close</button>
          {interview.error && interview.error.retry && <button className='ls-f-go' type='button' onClick={interview.tryAgain}>Try again</button>}
        </>
      )
    }

    if (recStep === REC_STEP.CHOICE)
      return <button className='ls-f-text' type='button' onClick={() => setChosenRecStep(REC_STEP.DONE)}>{TAKEOVER_COPY.record.notNow}</button>

    if (recStep === REC_STEP.RECORD) {
      return (
        <>
          <span className='ls-f-hint2'>{readingHint({mode: recordVersion.mode, state: recordVersion.recorder.state})}</span>
          <RecordControls big pauseFirst className='ls-f-rec' recorder={recordVersion.recorder} speakRef={goRef} />
        </>
      )
    }

    if (recStep === REC_STEP.DECIDE)
      return <DecisionButtons wide useRef={useButtonRef} onKeep={recordVersion.keepCurrent} onUse={recordVersion.useNew} />

    if (phase === INTERVIEW_PHASE.DONE)
      return <button className='ls-f-go' ref={goRef} type='button' onClick={handleClose}>Back to my story</button>

    return null
  }

  const footer = renderFooter()

  return (
    <div aria-label={TAKEOVER_COPY.label} aria-modal='true' className='ls-to' ref={rootRef} role='dialog' onKeyDown={handleKeyDown}>
      <div className='ls-to-bar'>
        <button aria-label='Back' className='ls-to-back' disabled={!canBack} style={canBack ? undefined : {visibility: 'hidden'}} type='button' onClick={handleBack}>
          <BackIcon />Back
        </button>
        <div aria-live='polite' className='ls-to-prog'>
          <span className='ls-to-prog-l'>{progress.label}</span>
          <span aria-hidden='true' className='ls-to-track'>
            <span className='ls-to-fill' style={{width: `${Math.max(4, (progress.position / progress.total) * 100)}%`}} />
          </span>
        </div>
        <button aria-label='Close' className='ls-icon-btn ls-mini-btn ls-to-close' disabled={recorderLocked} type='button' onClick={handleClose}>
          <CloseIcon size={16} />
        </button>
      </div>
      <div className='ls-to-main'>
        <div className='ls-to-col'>{renderMain()}</div>
      </div>
      <div className='ls-to-foot'>
        {footer && <div className='ls-to-col ls-to-foot-in'>{footer}</div>}
      </div>
    </div>
  )
}

EnrichTakeover.propTypes = {
  finePointer: PropTypes.bool,
  interview: PropTypes.shape({
    accept: PropTypes.func.isRequired,
    accepted: PropTypes.bool.isRequired,
    active: PropTypes.number.isRequired,
    activeItem: PropTypes.object,
    addExample: PropTypes.func.isRequired,
    answeredCount: PropTypes.number.isRequired,
    backToQuestions: PropTypes.func.isRequired,
    cancel: PropTypes.func.isRequired,
    error: PropTypes.object,
    errorMessage: PropTypes.string,
    finish: PropTypes.func.isRequired,
    go: PropTypes.func.isRequired,
    goDisabled: PropTypes.bool,
    goLabel: PropTypes.string,
    goPrimary: PropTypes.bool,
    items: PropTypes.array,
    jump: PropTypes.func.isRequired,
    marks: PropTypes.arrayOf(PropTypes.string).isRequired,
    phase: PropTypes.string.isRequired,
    setAnswer: PropTypes.func.isRequired,
    tryAgain: PropTypes.func.isRequired,
    woven: PropTypes.string.isRequired
  }).isRequired,
  currentRecording: PropTypes.object,
  recordVersion: PropTypes.shape({
    beginTakeover: PropTypes.func.isRequired,
    chooseMode: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    deciding: PropTypes.bool.isRequired,
    keepCurrent: PropTypes.func.isRequired,
    mode: PropTypes.string.isRequired,
    points: PropTypes.arrayOf(PropTypes.string),
    pointsLoading: PropTypes.bool,
    recorder: PropTypes.object.isRequired,
    result: PropTypes.string,
    take: PropTypes.object,
    useNew: PropTypes.func.isRequired
  }),
  storyText: PropTypes.string
}

export default EnrichTakeover
